import { css } from "@emotion/react";
import styled from "@emotion/styled";

import { fontAngkor, theme, transientOptions } from "shared/lib/styles";

import { EntityLink } from "scmp-app/components/entity-link";
import { Button } from "scmp-app/components/posties/button";

import DialogDesktop from "./dialog-desktop.svg";
import DialogMobile from "./dialog-mobile.svg";
import DialogTablet from "./dialog-tablet.svg";

type ItemProps = {
  $isEven: boolean;
};

export const Container = styled(EntityLink, { ...transientOptions })<ItemProps>`
  display: flex;
  justify-content: center;
  ${props =>
    props.$isEven
      ? css`
          flex-direction: column;
          ${theme.breakpoints.up("tablet")} {
            flex-direction: row;
          }
        `
      : css`
          flex-direction: column;
          ${theme.breakpoints.up("tablet")} {
            flex-direction: row-reverse;
          }
        `}
`;

const RotateIfOdd = (props: ItemProps) => css`
  ${props.$isEven
    ? css`
        transform: rotate(0);
      `
    : css`
        ${theme.breakpoints.up("tablet")} {
          transform: rotate(180deg);
        }
      `};
`;

export const StyledDialog = {
  Desktop: styled(DialogDesktop, { ...transientOptions })<ItemProps>`
    ${RotateIfOdd};
    display: none;
    ${theme.breakpoints.up("desktop")} {
      display: block;
    }
  `,
  Mobile: styled(DialogMobile, { ...transientOptions })<ItemProps>`
    ${RotateIfOdd};
    ${theme.breakpoints.up("tablet")} {
      display: none;
    }
  `,
  Tablet: styled(DialogTablet, { ...transientOptions })<ItemProps>`
    ${RotateIfOdd};
    display: none;
    ${theme.breakpoints.up("tablet")} {
      display: block;
    }
    ${theme.breakpoints.up("desktop")} {
      display: none;
    }
  `,
};

export const DialogContainer = styled.div`
  position: relative;
  inset-block-start: 0;
  inset-inline-start: 0;

  inline-size: 335px;
  margin: auto;
  ${theme.breakpoints.up("tablet")} {
    inline-size: 543px;
    block-size: 280px;
    margin: initial;
  }

  ${theme.breakpoints.up("desktop")} {
    inline-size: 826px;
    block-size: 248px;
  }
`;

export const AvatarContainer = styled.div<ItemProps>`
  display: flex;
  align-items: center;

  margin: auto;
  padding-block-start: 12px;
  ${theme.breakpoints.up("tablet")} {
    margin: initial;
    padding-block-start: 0;
  }

  ${props =>
    props.$isEven
      ? css`
          ${theme.breakpoints.up("tablet")} {
            padding-inline-start: 16px;
          }
        `
      : css`
          ${theme.breakpoints.up("tablet")} {
            padding-inline-end: 16px;
          }
        `}
`;

const AvatarStyle = css`
  inline-size: 78px;
  block-size: 78px;
  ${theme.breakpoints.up("tablet")} {
    inline-size: 104px;
    block-size: 104px;
  }
`;

export const StyledAvatarImage = styled.img`
  ${AvatarStyle}
`;

export const TextContainer = styled.div<ItemProps>`
  position: absolute;
  inset-block-start: 0;
  inset-inline-start: 0;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;

  inline-size: 100%;
  block-size: 100%;
  padding-block: 28px 62px;
  padding-inline: 28px;

  ${theme.breakpoints.up("tablet")} {
    padding-block: 32px;
  }

  ${theme.breakpoints.up("desktop")} {
    padding-block: 31px;
  }

  ${props =>
    props.$isEven
      ? css`
          ${theme.breakpoints.up("tablet")} {
            padding-inline: 35px 61px;
          }
        `
      : css`
          ${theme.breakpoints.up("tablet")} {
            padding-inline: 61px 35px;
          }
        `}
`;

export const HeadlineContainer = styled.div<ItemProps>`
  display: -webkit-box;

  margin-block-end: 16px;

  text-overflow: ellipsis;

  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 5;
  line-clamp: 5;

  ${theme.breakpoints.up("tablet")} {
    -webkit-line-clamp: 4;
    line-clamp: 4;
  }

  ${theme.breakpoints.up("desktop")} {
    -webkit-line-clamp: 3;
    line-clamp: 3;
  }

  ${props =>
    props.$isEven
      ? css`
          color: #389810;
        `
      : css`
          color: #0f75bf;
        `};
`;

export const Headline = styled.div`
  font-weight: 400;
  font-size: 24px;
  font-family: ${fontAngkor};
  font-style: normal;
  line-height: 28px;
  letter-spacing: -0.72px;
  ${theme.breakpoints.up("tablet")} {
    font-size: 32px;
    line-height: 38px;
    letter-spacing: -0.96px;
  }
  ${theme.breakpoints.up("desktop")} {
    font-size: 32px;
    line-height: 44px;
  }
`;

export const StyledButton = styled(Button)`
  display: block;
`;
